import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CheckoutOrderFactory } from './factory.service';
import { CheckoutOrderValidationError, CheckoutOrderValidationRule } from './enum/checkout-order-validation.enum';
import {
	CheckoutOrderValidationContext,
	CheckoutServiceAvailability,
} from './interfaces/checkout-order-validation.interface';
import { Availability, Coupon, CouponValidation, MenuItem, PaymentResult, VaultedCard } from '@core/models';
import { MenuItemCharges } from '@core/models/menu-item/menu-item';
import {
	CheckoutCouponValidationContext,
	CheckoutCouponValidationResult,
} from './interfaces/checkout-coupon-validation.interface';
import {
	CheckoutAssociatedCardInput,
	CheckoutAssociatedCardGateway,
	CheckoutAssociatedCardResponseContext,
	CheckoutVaultedCardCreationContext,
} from './interfaces/checkout-credit-card.interface';
import { IResponseObjectSuccess } from '@core/interfaces';
import { createAssociatedCardRequest } from './util';
import {
	CheckoutPaymentInput,
	CheckoutPaymentPlan,
} from './interfaces/checkout-payment.interface';
import {
	CheckoutValidationContext,
	CheckoutValidationInput,
	CheckoutValidationResult,
} from './interfaces/checkout-validation-flow.interface';
import { CheckoutPaymentPlanType } from './enum/checkout-flow.enum';
import { CheckoutValidationException } from './checkout-validation.exception';
import { getCheckoutOrderValidationMessage } from './util';
import {
	CheckoutOrderCreationConfiguration,
	CheckoutOrderCreationDataInput,
} from './interfaces/checkout-order-creation.interface';
import {
	createCheckoutOrderCreationConfiguration,
	filterCheckoutCatalogResponse,
	filterCheckoutOrderTypes,
} from './rules/checkout-order-creation.domain';
import { OrderType, OrderUpsert } from '@core/models';
import { OrderTypeCodeEnum } from '@core/enums';
import { IntegerPrecisionService } from '@core/services';
import { CartProcessorService } from '@/features/shoppingcart/domain';
import { menuItemProcessor } from '@/core/domain/menu';
import { detectCheckoutPriceChanges } from './util';

export type CheckoutPriceLookupResult = {
	updatedMenuItems: MenuItem[];
	priceChanges: MenuItemCharges[];
};

@Injectable({ providedIn: 'root' })
export class CheckoutOrderProcessor {
	constructor(private readonly checkoutOrderFactory: CheckoutOrderFactory) {}

	createOrderCreationConfiguration(
		paymentMethod: string,
		orderTypeName: string | undefined
	): CheckoutOrderCreationConfiguration {
		return createCheckoutOrderCreationConfiguration(
			this.checkoutOrderFactory.createOrderCreationRules(),
			{ paymentMethod, orderTypeName }
		);
	}

	filterOrderCatalogResponse<T extends { nombre: string }>(
		data: T[],
		filterName: string
	): T[] {
		return filterCheckoutCatalogResponse(data, filterName);
	}

	filterOrderTypes(orderTypes: OrderType[], orderTypeCode: OrderTypeCodeEnum): OrderType[] {
		return filterCheckoutOrderTypes(orderTypes, orderTypeCode);
	}

	createOrderPayload(input: CheckoutOrderCreationDataInput): OrderUpsert {
		const [firstRule, ...remainingRules] = this.checkoutOrderFactory.createOrderPayloadRules();
		let payload = firstRule(input);
		for (const rule of remainingRules) {
			payload = rule(input);
		}
		return payload;
	}

	validateOrder(context: CheckoutOrderValidationContext): CheckoutOrderValidationError | null {
		return this.execute(context, this.checkoutOrderFactory.createOrderValidationRules());
	}

	validateWorkDay(context: CheckoutOrderValidationContext): CheckoutOrderValidationError | null {
		return this.execute(context, this.checkoutOrderFactory.createWorkDayValidationRules());
	}

	validateServiceAvailability(
		context: CheckoutOrderValidationContext & {
			serviceAvailability: CheckoutServiceAvailability;
		}
	): CheckoutOrderValidationError | null {
		return this.execute(context, this.checkoutOrderFactory.createServiceAvailabilityRules());
	}

	processCheckoutValidation(input: CheckoutValidationInput): CheckoutValidationResult {
		const initialContext: CheckoutValidationContext = {
			...input,
			isAvailabilitySuccessful: false,
			priceLookupSucceeded: false,
			availabilityErrorMessage: null,
			priceLookupErrorMessage: null,
			priceChanges: [],
			updatedMenuItems: [],
			workDayChecked: false,
			workDay: null,
			serviceAvailabilityChecked: false,
			serviceAvailability: null,
			updatedStockItems: null,
			orderValidationContext: null,
			failure: null,
			preparedOrder: null,
			paymentPlan: null,
			isReady: false,
		};

		const result = this.checkoutOrderFactory
			.createCheckoutValidationRules()
			.reduce((currentContext, rule) => rule(currentContext), initialContext);
		if (result.failure) {
			return this.createCheckoutValidationFailure(result, result.failure);
		}
		const preparedOrder = result.preparedOrder;
		if (!result.isReady || !preparedOrder) {
			return this.createCheckoutValidationFailure(
				result,
				new CheckoutValidationException(
					'The order could not be validated',
					'No se pudo validar la orden',
					'error'
				)
			);
		}

		if (result.paymentAmount === null) {
			const error = CheckoutOrderValidationError.OrderDataIncomplete;
			return this.createCheckoutValidationFailure(
				result,
				new CheckoutValidationException(
					getCheckoutOrderValidationMessage(error, result.unavailableProducts),
					'No se pudo validar la orden',
					'error'
				)
			);
		}

		const paymentPlan = this.createCheckoutPaymentPlan({
			order: preparedOrder,
			amount: result.paymentAmount,
			isLoggedIn: result.isLoggedIn,
			selectedCardToken: result.selectedCardToken,
			defaultCardToken: result.defaultCardToken,
			associatedCardToken: result.associatedCardToken,
		});
		if (paymentPlan.type === CheckoutPaymentPlanType.Invalid) {
			const error = paymentPlan.error;
			return this.createCheckoutValidationFailure(
				result,
				new CheckoutValidationException(
					getCheckoutOrderValidationMessage(error, result.unavailableProducts),
					'Payment information incomplete',
					'error'
				)
			);
		}

		return { ...result, preparedOrder, paymentPlan, failure: null, isReady: true };
	}

	processCheckoutPriceLookup(
		updatedMenuItems: MenuItem[],
		cartItems: MenuItem[],
		availabilityList: Availability[] | null,
		precisionService: IntegerPrecisionService,
		cartProcessor: CartProcessorService
	): CheckoutPriceLookupResult {
		const catalogItems = menuItemProcessor.processCatalog(
			updatedMenuItems,
			availabilityList ?? [],
			precisionService,
			new Date()
		);
		const { menuItems } = cartProcessor.calculateCartsWithSubtotalPrice(catalogItems);

		return {
			updatedMenuItems: menuItems,
			priceChanges: detectCheckoutPriceChanges(menuItems, cartItems),
		};
	}

	private createCheckoutValidationFailure(
		context: CheckoutValidationContext,
		failure: CheckoutValidationException
	): CheckoutValidationResult {
		return {
			...context,
			failure,
			preparedOrder: null,
			paymentPlan: null,
			isReady: false,
		};
	}

	createCheckoutPaymentPlan(input: CheckoutPaymentInput): CheckoutPaymentPlan {
		return this.checkoutOrderFactory
			.createCheckoutPaymentStrategy(input.order.paymentMethod)
			.prepare(input);
	}

	acceptsCheckoutPaymentResult(paymentMethod: string, result: PaymentResult): boolean {
		return this.checkoutOrderFactory
			.createCheckoutPaymentStrategy(paymentMethod)
			.acceptsResult(result);
	}

	processCouponValidation(input: {
		coupon: Coupon;
		validation: CouponValidation;
		cartSubtotal: bigint | null;
	}): CheckoutCouponValidationResult {
		const initialContext: CheckoutCouponValidationContext = {
			...input,
			couponDiscount: 0n,
			isApplicable: false,
			validatedCoupon: null,
		};
		const result = this.checkoutOrderFactory
			.createCouponValidationRules()
			.reduce<CheckoutCouponValidationContext>((context, rule) => rule(context), initialContext);

		return {
			isApplicable: result.isApplicable,
			validatedCoupon: result.validatedCoupon!,
		};
	}

	createAssociatedCard(
		input: CheckoutAssociatedCardInput,
		gateway: CheckoutAssociatedCardGateway
	): Observable<CheckoutAssociatedCardResponseContext> {
		const request = createAssociatedCardRequest(input);
		const strategy = this.checkoutOrderFactory.createAssociatedCardStrategy(input.isLoggedIn);

		return strategy.create(request, gateway).pipe(
			map(response => {
				const initialContext: CheckoutAssociatedCardResponseContext = {
					response,
					customerMerchId: request.customerMerchId,
					normalizedResponse: response,
					vaultedCardRequest: null,
				};
				return this.checkoutOrderFactory
					.createAssociatedCardResponseRules()
					.reduce<CheckoutAssociatedCardResponseContext>((context, rule) => rule(context), initialContext);
			})
		);
	}

	processVaultedCardCreationResponse(response: IResponseObjectSuccess<VaultedCard>): VaultedCard {
		const initialContext: CheckoutVaultedCardCreationContext = { response };
		const result = this.checkoutOrderFactory
			.createVaultedCardCreationRules()
			.reduce<CheckoutVaultedCardCreationContext>((context, rule) => rule(context), initialContext);
		return result.response.data;
	}

	private execute(
		context: CheckoutOrderValidationContext,
		rules: CheckoutOrderValidationRule[]
	): CheckoutOrderValidationError | null {
		for (const rule of rules) {
			const error = rule(context);
			if (error) {
				return error;
			}
		}

		return null;
	}
}
